/**
 * 商业底座 API 契约：套餐 / 加购 / 订阅 / 订购 / 账单 / 支付 / 退款 / 用量 / 例外 / 对账。
 *
 * 硬性约定（ADR-0003 金额与数量的表示）：
 *   1. 所有金额字段均为最小货币单位整数（人民币为「分」），禁止浮点。
 *      线上唯一格式是十进制数字字符串（如 "1990" 表示 19.90 元）：
 *      数据库列是 PostgreSQL bigint，服务端用原生 BigInt 读写；
 *      JSON 无法序列化 BigInt，字符串契约让金额安全穿越 HTTP 边界，
 *      且不受 JS number 2^53 精度上限影响。
 *   2. 税率使用基点（万分之一）。
 *   3. 写操作一律要求幂等键（idempotencyKey），重复提交返回同一结果。
 *   4. 写入类请求必须携带业务原因（reason），用于审计与双人审批。
 *   5. 契约只描述控制面数据，任何租户业务明细都不在此出现。
 */
import { z } from "zod";
/**
 * 最小货币单位金额：非负十进制整数字符串，最长 18 位（覆盖 PostgreSQL bigint 正域）。
 * 示例："1990" = 19.90 元。拒绝 number / 浮点 / 负数 / 前导零。
 */
export const moneyMinorSchema = z.string().regex(/^(0|[1-9][0-9]{0,17})$/, "金额必须是最小货币单位的非负整数字符串");
/** 可正可负的金额差（差额、调整额）。 */
export const signedMoneyMinorSchema = z.string().regex(/^-?(0|[1-9][0-9]{0,17})$/, "金额差额必须是不超过 18 位的整数字符串");
/** 大数量（用量、包含量）：非负十进制整数字符串，与金额同理由使用字符串。 */
export const quantitySchema = z.string().regex(/^(0|[1-9][0-9]{0,17})$/, "数量必须是非负整数字符串");
/** 金额/数量字符串 → BigInt。调用前应已通过 schema 校验。 */
export function parseAmountMinor(value) {
    return BigInt(value);
}
/** BigInt → 金额/数量字符串（JSON 安全）。 */
export function formatAmountMinor(value) {
    return value.toString();
}
/** 两个金额字符串比较：-1 / 0 / 1。调用前应已通过 schema 校验。 */
export function compareAmountMinor(left, right) {
    const a = BigInt(left);
    const b = BigInt(right);
    return a < b ? -1 : a > b ? 1 : 0;
}
/** 税率基点：0 ~ 10000（0% ~ 100%）。基数小，number 即安全。 */
export const taxRateBpSchema = z.number().int().min(0).max(10_000);
/** 币种：ISO 4217 三字母大写。 */
export const currencySchema = z.string().regex(/^[A-Z]{3}$/);
/** 幂等键。 */
export const idempotencyKeySchema = z.string().trim().min(8).max(160);
/** 业务原因，进入审计。 */
export const reasonSchema = z.string().trim().min(8).max(500);
/** 时间字符串。 */
const at = z.string().datetime();
const atOrNull = at.nullable();
/* ------------------------------------------------------------------ *
 * 枚举
 * ------------------------------------------------------------------ */
export const billingIntervals = ["monthly", "yearly", "one_time", "contract"];
export const billingIntervalSchema = z.enum(billingIntervals);
export const billingIntervalLabels = {
    monthly: "按月",
    yearly: "按年",
    one_time: "一次性",
    contract: "合同制",
};
export const taxModes = ["tax_inclusive", "tax_exclusive"];
export const taxModeSchema = z.enum(taxModes);
export const planVersionStatuses = ["draft", "pending_publish", "published", "stop_new_sales", "archived"];
export const planVersionStatusSchema = z.enum(planVersionStatuses);
export const planVersionStatusLabels = {
    draft: "草稿",
    pending_publish: "待发布",
    published: "已发布",
    stop_new_sales: "停止新购",
    archived: "已归档",
};
export const capabilityInclusions = ["included", "addon_available", "unavailable"];
export const capabilityInclusionSchema = z.enum(capabilityInclusions);
export const capabilityInclusionLabels = {
    included: "已包含",
    addon_available: "可加购",
    unavailable: "不可用",
};
export const overagePolicies = ["alert_only", "block_new", "suspend_feature", "metered_billing"];
export const overagePolicySchema = z.enum(overagePolicies);
export const overagePolicyLabels = {
    alert_only: "仅提醒",
    block_new: "限制新操作",
    suspend_feature: "暂停功能",
    metered_billing: "按量计费",
};
export const limitResetPeriods = ["monthly", "billing_period", "never"];
export const limitResetPeriodSchema = z.enum(limitResetPeriods);
export const limitResetPeriodLabels = {
    monthly: "每月重置",
    billing_period: "每账期重置",
    never: "不重置（累计）",
};
export const meterAggregations = ["sum", "max", "last", "unique_count"];
export const meterAggregationSchema = z.enum(meterAggregations);
export const meterSources = ["control_plane", "erp_runtime"];
export const meterSourceSchema = z.enum(meterSources);
export const meterLifecycles = ["draft", "active", "retired"];
export const meterLifecycleSchema = z.enum(meterLifecycles);
export const quantityDecimalScaleSchema = z.number().int().min(0).max(6);
export const addonKinds = ["usage", "seats", "feature", "service_period"];
export const addonKindSchema = z.enum(addonKinds);
export const addonKindLabels = {
    usage: "额外用量",
    seats: "额外席位",
    feature: "额外功能",
    service_period: "延长服务期",
};
export const subscriptionStatusesV2 = [
    "trialing", "active", "past_due", "grace_period", "suspended", "pending_cancel", "canceled", "expired", "activation_error", "awaiting_activation",
];
export const subscriptionStatusV2Schema = z.enum(subscriptionStatusesV2);
export const subscriptionStatusLabels = {
    trialing: "试用中",
    active: "生效中",
    past_due: "待续费",
    grace_period: "宽限期",
    suspended: "已暂停",
    pending_cancel: "待取消",
    canceled: "已取消",
    expired: "已到期",
    activation_error: "生效异常",
    awaiting_activation: "等待客户激活",
};
export const subscriptionChangeTypes = [
    "upgrade", "downgrade", "renew", "suspend", "resume", "cancel", "undo_pending", "manual_extend", "force_migrate",
];
export const subscriptionChangeTypeSchema = z.enum(subscriptionChangeTypes);
export const subscriptionChangeRequestTypes = [
    "upgrade", "downgrade", "renew", "pause", "resume", "cancel", "revoke_pending", "extend",
];
export const subscriptionChangeRequestTypeSchema = z.enum(subscriptionChangeRequestTypes);
export const subscriptionChangeTypeLabels = {
    upgrade: "升级套餐",
    downgrade: "降级套餐",
    renew: "续费",
    suspend: "暂停服务",
    pause: "暂停服务",
    resume: "恢复服务",
    cancel: "取消订阅",
    undo_pending: "撤销待生效变更",
    revoke_pending: "撤销待生效变更",
    manual_extend: "手工延长",
    extend: "手工延长",
    force_migrate: "强制迁移套餐",
};
export const subscriptionChangeStatuses = [
    "draft", "pending_approval", "pending_payment", "pending_effective", "applying", "applied", "failed", "canceled", "reverted",
];
export const subscriptionChangeStatusSchema = z.enum(subscriptionChangeStatuses);
export const subscriptionChangeStatusLabels = {
    draft: "草稿",
    pending_approval: "待审批",
    pending_payment: "待支付",
    pending_effective: "待生效",
    applying: "应用中",
    applied: "已应用",
    failed: "执行失败",
    canceled: "已撤销",
    reverted: "已回滚",
};
export const prorationModes = ["none", "daily", "full_period"];
export const prorationModeSchema = z.enum(prorationModes);
export const prorationModeLabels = {
    none: "不折算",
    daily: "按天折算",
    full_period: "整期计价",
};
export const purchaseOrderStatuses = [
    "draft", "pending_payment", "pending_offline_confirm", "paid", "provisioning", "completed", "canceled", "refunded", "exception",
];
export const purchaseOrderStatusSchema = z.enum(purchaseOrderStatuses);
export const purchaseOrderStatusLabels = {
    draft: "草稿",
    pending_payment: "待支付",
    pending_offline_confirm: "待确认收款",
    paid: "已收款",
    provisioning: "开通中",
    completed: "已完成",
    canceled: "已取消",
    refunded: "已退款",
    exception: "异常",
};
export const purchaseChannels = ["platform_sales", "self_service", "partner", "renewal"];
export const purchaseChannelSchema = z.enum(purchaseChannels);
export const purchaseChannelLabels = {
    platform_sales: "平台销售",
    self_service: "自助购买",
    partner: "渠道伙伴",
    renewal: "续费",
};
export const invoiceStatuses = ["draft", "open", "paid", "partially_refunded", "refunded", "void", "uncollectible"];
export const invoiceStatusSchema = z.enum(invoiceStatuses);
export const invoiceStatusLabels = {
    draft: "草稿",
    open: "待收款",
    paid: "已收款",
    partially_refunded: "部分退款",
    refunded: "已退款",
    void: "已作废",
    uncollectible: "无法收回",
};
export const paymentMethods = ["offline_confirm", "bank_transfer", "wechat_pay", "alipay", "other"];
export const paymentMethodSchema = z.enum(paymentMethods);
export const paymentMethodLabels = {
    offline_confirm: "线下确认收款",
    bank_transfer: "银行转账",
    wechat_pay: "微信支付",
    alipay: "支付宝",
    other: "其他",
};
export const paymentStatuses = ["pending", "succeeded", "failed", "refunded", "canceled"];
export const paymentStatusSchema = z.enum(paymentStatuses);
export const paymentStatusLabels = {
    pending: "处理中",
    succeeded: "已到账",
    failed: "失败",
    refunded: "已退款",
    canceled: "已取消",
};
export const billingWebhookProcessingStatuses = ["received", "processed", "ignored", "failed"];
export const billingWebhookProcessingStatusSchema = z.enum(billingWebhookProcessingStatuses);
export const billingWebhookReceiveHeaderSchema = z.object({
    provider: paymentMethodSchema.exclude(["offline_confirm"]),
    eventKey: z.string().trim().min(8).max(200).optional(),
    timestamp: z.string().trim().min(10).max(80).optional(),
    signature: z.string().trim().min(8).max(240).optional(),
});
export const billingWebhookReceiptSchema = z.object({
    id: z.string().uuid(),
    provider: z.string(),
    eventKey: z.string(),
    signatureValid: z.boolean(),
    processingStatus: billingWebhookProcessingStatusSchema,
    rawPayloadDigest: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    receivedAt: at,
    processedAt: atOrNull,
    replayed: z.boolean(),
    message: z.string(),
});
export const refundStatuses = ["pending", "approved", "rejected", "succeeded", "failed"];
export const refundStatusSchema = z.enum(refundStatuses);
export const refundStatusLabels = {
    pending: "待审核",
    approved: "已批准",
    rejected: "已驳回",
    succeeded: "已退款",
    failed: "退款失败",
};
export const refundSubscriptionHandlings = ["none", "suspend", "cancel"];
export const refundSubscriptionHandlingSchema = z.enum(refundSubscriptionHandlings);
export const overrideEffects = ["grant", "revoke"];
export const overrideEffectSchema = z.enum(overrideEffects);
export const reconciliationDiffStatuses = ["open", "resolved", "ignored"];
export const reconciliationDiffStatusSchema = z.enum(reconciliationDiffStatuses);
/** 页面只展示这些中文状态，禁止把枚举代码直接渲染给操作者。 */
export const commercialStatusLabelMaps = {
    planVersion: planVersionStatusLabels,
    subscription: subscriptionStatusLabels,
    subscriptionChange: subscriptionChangeStatusLabels,
    purchaseOrder: purchaseOrderStatusLabels,
    invoice: invoiceStatusLabels,
    payment: paymentStatusLabels,
    refund: refundStatusLabels,
};
/* ------------------------------------------------------------------ *
 * 计量目录
 * ------------------------------------------------------------------ */
const usageMeterCatalogDefinitionSchema = z.object({
    meterKey: z.string().regex(/^[a-z0-9][a-z0-9_.:-]{1,119}$/),
    capabilityId: z.string().uuid().nullable().default(null),
    displayName: z.string().trim().min(2).max(120),
    unit: z.string().trim().min(1).max(40),
    aggregation: meterAggregationSchema,
    source: meterSourceSchema,
    decimalScale: quantityDecimalScaleSchema.default(0),
    dedupeKeyRequired: z.boolean().default(true),
    billable: z.boolean().default(false),
    dataLatencyMinutes: z.number().int().min(0).max(1440).default(0),
    lifecycle: meterLifecycleSchema.default("draft"),
});
export const usageMeterCatalogUpsertSchema = usageMeterCatalogDefinitionSchema.extend({
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const usageMeterCatalogViewSchema = usageMeterCatalogDefinitionSchema.extend({
    id: z.string().uuid(),
    capabilityName: z.string().nullable(),
    updatedAt: at,
});
/* ------------------------------------------------------------------ *
 * 套餐产品与版本
 * ------------------------------------------------------------------ */
export const planProductCreateSchema = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(1_000).default(""),
    /** 内部编号留空时由服务端生成，普通操作者不可见。 */
    code: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,79}$/).optional(),
});
export const planVersionCreateSchema = z.object({
    changeNote: z.string().trim().max(1_000).default(""),
    trialDays: z.number().int().min(0).max(365).default(0),
    gracePeriodDays: z.number().int().min(0).max(180).default(0),
    autoRenewDefault: z.boolean().default(true),
    saleChannels: z.array(z.string().max(60)).max(20).default([]),
    saleRegions: z.array(z.string().max(60)).max(50).default([]),
    /** 从已有版本复制，避免从零勾选。 */
    copyFromVersionId: z.string().uuid().nullable().default(null),
});
const planCapabilityReplaceItemSchema = z.object({
    capabilityId: z.string().uuid(),
    inclusion: capabilityInclusionSchema,
});
const planMeterLimitReplaceItemSchema = z.object({
    meterId: z.string().uuid(),
    unlimited: z.boolean().default(false),
    includedQuantity: quantitySchema.default("0"),
    softLimitPercent: z.number().int().min(1).max(100).default(80),
    hardLimitQuantity: quantitySchema.nullable().default(null),
    overagePolicy: overagePolicySchema.default("alert_only"),
    overageUnitPriceMinor: moneyMinorSchema.nullable().default(null),
    resetPeriod: limitResetPeriodSchema.default("billing_period"),
});
const planPriceReplaceItemSchema = z.object({
    billingInterval: billingIntervalSchema,
    currency: currencySchema.default("CNY"),
    amountMinor: moneyMinorSchema,
    setupFeeMinor: moneyMinorSchema.default("0"),
    taxMode: taxModeSchema.default("tax_inclusive"),
    taxRateBp: taxRateBpSchema.default(0),
    validFrom: at,
    validTo: atOrNull.default(null),
});
const planAddonOfferReplaceItemSchema = z.object({ addonVersionId: z.string().uuid() });
export const planCapabilitiesReplaceSchema = z.object({
    items: z.array(planCapabilityReplaceItemSchema).min(1).max(500),
});
export const planMeterLimitsReplaceSchema = z.object({
    items: z.array(planMeterLimitReplaceItemSchema).max(200),
}).superRefine((value, ctx) => {
    const seen = new Set();
    for (const [index, item] of value.items.entries()) {
        if (seen.has(item.meterId)) {
            ctx.addIssue({ code: "custom", path: ["items", index, "meterId"], message: "同一个用量项目不能重复配置" });
        }
        seen.add(item.meterId);
        if (item.overagePolicy === "metered_billing" && item.overageUnitPriceMinor === null) {
            ctx.addIssue({ code: "custom", path: ["items", index, "overageUnitPriceMinor"], message: "按量计费必须填写超额单价" });
        }
        if (item.hardLimitQuantity !== null && parseAmountMinor(item.hardLimitQuantity) < parseAmountMinor(item.includedQuantity)) {
            ctx.addIssue({ code: "custom", path: ["items", index, "hardLimitQuantity"], message: "硬限制不能低于包含量" });
        }
    }
});
export const planPricesReplaceSchema = z.object({
    items: z.array(planPriceReplaceItemSchema).min(1).max(100),
}).superRefine((value, ctx) => {
    for (const [index, item] of value.items.entries()) {
        if (item.validTo && item.validTo <= item.validFrom) {
            ctx.addIssue({ code: "custom", path: ["items", index, "validTo"], message: "结束时间必须晚于开始时间" });
        }
    }
});
/**
 * 套餐发布前完整性检查（CP-PLAN-011）。
 * 服务端必须用同一份规则复算；前端的检查只是提前反馈，不作为放行依据。
 */
export const planPublishSchema = z.object({
    versionId: z.string().uuid(),
    /** 确认已知晓影响范围；影响租户数为 0 时不强制。 */
    acknowledgedImpactTenantCount: z.number().int().nonnegative(),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const planVersionDiffQuerySchema = z.object({
    fromVersionId: z.string().uuid(),
    toVersionId: z.string().uuid(),
});
/** 发布检查结果。任何一项不通过都必须阻断发布并给出可执行的修复动作。 */
export const planPublishCheckSchema = z.object({
    ready: z.boolean(),
    blocking: z.array(z.object({
        code: z.string(),
        message: z.string(),
        fixAction: z.string(),
    })),
    warnings: z.array(z.object({
        code: z.string(),
        message: z.string(),
    })),
    impactTenantCount: z.number().int().nonnegative(),
    impactSubscriptionCount: z.number().int().nonnegative(),
});
export const planVersionDraftUpdateSchema = planVersionCreateSchema.omit({ copyFromVersionId: true });
export const planVersionLifecycleSchema = z.object({
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const planAddonOffersReplaceSchema = z.object({
    items: z.array(planAddonOfferReplaceItemSchema).max(100),
}).superRefine((value, ctx) => {
    const seen = new Set();
    for (const [index, item] of value.items.entries()) {
        if (seen.has(item.addonVersionId)) {
            ctx.addIssue({ code: "custom", path: ["items", index, "addonVersionId"], message: "同一个加购包不能重复关联" });
        }
        seen.add(item.addonVersionId);
    }
});
const planVersionDraftFullSaveBaseSchema = planVersionDraftUpdateSchema.extend({
    capabilities: z.array(planCapabilityReplaceItemSchema).min(1).max(500),
    meterLimits: z.array(planMeterLimitReplaceItemSchema).max(200),
    prices: z.array(planPriceReplaceItemSchema).min(1).max(100),
    addonOffers: z.array(planAddonOfferReplaceItemSchema).max(100).default([]),
});
function refinePlanDraftFull(value, ctx) {
    const capabilities = new Set();
    for (const [index, item] of value.capabilities.entries()) {
        if (capabilities.has(item.capabilityId))
            ctx.addIssue({ code: "custom", path: ["capabilities", index, "capabilityId"], message: "同一个功能不能重复配置" });
        capabilities.add(item.capabilityId);
    }
    const meters = new Set();
    for (const [index, item] of value.meterLimits.entries()) {
        if (meters.has(item.meterId))
            ctx.addIssue({ code: "custom", path: ["meterLimits", index, "meterId"], message: "同一个用量项目不能重复配置" });
        meters.add(item.meterId);
        if (item.overagePolicy === "metered_billing" && item.overageUnitPriceMinor === null)
            ctx.addIssue({ code: "custom", path: ["meterLimits", index, "overageUnitPriceMinor"], message: "按量计费必须填写超额单价" });
        if (item.hardLimitQuantity !== null && parseAmountMinor(item.hardLimitQuantity) < parseAmountMinor(item.includedQuantity))
            ctx.addIssue({ code: "custom", path: ["meterLimits", index, "hardLimitQuantity"], message: "硬限制不能低于包含量" });
    }
    for (const [index, item] of value.prices.entries()) {
        if (item.validTo && item.validTo <= item.validFrom)
            ctx.addIssue({ code: "custom", path: ["prices", index, "validTo"], message: "结束时间必须晚于开始时间" });
    }
    const addons = new Set();
    for (const [index, item] of value.addonOffers.entries()) {
        if (addons.has(item.addonVersionId))
            ctx.addIssue({ code: "custom", path: ["addonOffers", index, "addonVersionId"], message: "同一个加购包不能重复关联" });
        addons.add(item.addonVersionId);
    }
}
export const planVersionDraftFullSaveSchema = planVersionDraftFullSaveBaseSchema.superRefine(refinePlanDraftFull);
export const planVersionDraftFullCreateSchema = planProductCreateSchema.extend(planVersionDraftFullSaveBaseSchema.shape).superRefine(refinePlanDraftFull);
export const planProductCatalogItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    status: z.enum(["draft", "published", "retired"]),
    versions: z.array(z.object({
        id: z.string().uuid(),
        version: z.number().int().positive(),
        status: planVersionStatusSchema,
        statusLabel: z.string(),
        changeNote: z.string(),
        trialDays: z.number().int().nonnegative(),
        gracePeriodDays: z.number().int().nonnegative(),
        autoRenewDefault: z.boolean(),
        saleChannels: z.array(z.string()),
        saleRegions: z.array(z.string()),
        publishedAt: atOrNull,
        capabilitiesCount: z.number().int().nonnegative(),
        meterLimitsCount: z.number().int().nonnegative(),
        pricesCount: z.number().int().nonnegative(),
        addonOffersCount: z.number().int().nonnegative(),
        affectedTenantCount: z.number().int().nonnegative(),
        updatedAt: at,
    })),
    updatedAt: at,
});
export const planVersionDetailSchema = z.object({
    id: z.string().uuid(),
    planId: z.string().uuid(),
    planName: z.string(),
    description: z.string(),
    version: z.number().int().positive(),
    status: planVersionStatusSchema,
    statusLabel: z.string(),
    changeNote: z.string(),
    trialDays: z.number().int().nonnegative(),
    gracePeriodDays: z.number().int().nonnegative(),
    autoRenewDefault: z.boolean(),
    saleChannels: z.array(z.string()),
    saleRegions: z.array(z.string()),
    publishedAt: atOrNull,
    capabilities: z.array(z.object({
        capabilityId: z.string().uuid(),
        domain: z.string(),
        domainLabel: z.string(),
        displayName: z.string(),
        description: z.string(),
        inclusion: capabilityInclusionSchema,
    })),
    meterLimits: z.array(z.object({
        meterId: z.string().uuid(),
        displayName: z.string(),
        unit: z.string(),
        unlimited: z.boolean(),
        includedQuantity: quantitySchema,
        softLimitPercent: z.number().int(),
        hardLimitQuantity: quantitySchema.nullable(),
        overagePolicy: overagePolicySchema,
        overageUnitPriceMinor: moneyMinorSchema.nullable(),
        resetPeriod: limitResetPeriodSchema,
    })),
    prices: z.array(z.object({
        billingInterval: billingIntervalSchema,
        currency: currencySchema,
        amountMinor: moneyMinorSchema,
        setupFeeMinor: moneyMinorSchema,
        taxMode: taxModeSchema,
        taxRateBp: taxRateBpSchema,
        validFrom: at,
        validTo: atOrNull,
    })),
    addonOffers: z.array(z.object({
        addonVersionId: z.string().uuid(),
        addonName: z.string(),
        kind: addonKindSchema,
        version: z.number().int().positive(),
        status: planVersionStatusSchema,
        billingInterval: billingIntervalSchema,
        amountMinor: moneyMinorSchema,
        currency: currencySchema,
    })),
    publishCheck: planPublishCheckSchema,
});
export const planVersionDiffSchema = z.object({
    fromVersionId: z.string().uuid(),
    toVersionId: z.string().uuid(),
    capabilityChanges: z.array(z.object({ name: z.string(), from: z.string(), to: z.string(), effect: z.string() })),
    meterLimitChanges: z.array(z.object({ name: z.string(), from: z.string(), to: z.string(), effect: z.string() })),
    priceChanges: z.array(z.object({ interval: z.string(), from: z.string(), to: z.string(), effect: z.string() })),
});
/* ------------------------------------------------------------------ *
 * 加购
 * ------------------------------------------------------------------ */
export const addonProductCreateSchema = z.object({
    code: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,79}$/).optional(),
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(1_000).default(""),
    kind: addonKindSchema,
});
const addonVersionCreateBaseSchema = z.object({
    changeNote: z.string().trim().max(1_000).default(""),
    meterId: z.string().uuid().nullable().default(null),
    capabilityId: z.string().uuid().nullable().default(null),
    /** seats = 席位数量；service_period = 延长天数；usage = 额外用量。 */
    quantity: quantitySchema.nullable().default(null),
    billingInterval: billingIntervalSchema,
    currency: currencySchema.default("CNY"),
    amountMinor: moneyMinorSchema,
    taxMode: taxModeSchema.default("tax_inclusive"),
    taxRateBp: taxRateBpSchema.default(0),
    validFrom: at,
    validTo: atOrNull.default(null),
});
export const addonVersionCreateSchema = addonVersionCreateBaseSchema.superRefine((value, ctx) => {
    const targets = [value.meterId, value.capabilityId, value.quantity].filter((item) => item !== null).length;
    if (targets !== 1) {
        ctx.addIssue({ code: "custom", path: ["quantity"], message: "加购项只能在用量项目、功能项和数量之间选择一种落点" });
    }
    if (value.validTo && value.validTo <= value.validFrom) {
        ctx.addIssue({ code: "custom", path: ["validTo"], message: "结束时间必须晚于开始时间" });
    }
});
export const addonVersionDraftUpdateSchema = addonVersionCreateSchema;
export const addonDraftFullCreateSchema = addonProductCreateSchema.extend(addonVersionCreateBaseSchema.shape).superRefine((value, ctx) => {
    const targets = [value.meterId, value.capabilityId, value.quantity].filter((item) => item !== null).length;
    if (targets !== 1)
        ctx.addIssue({ code: "custom", path: ["quantity"], message: "加购项只能在用量项目、功能项和数量之间选择一种落点" });
    if (value.validTo && value.validTo <= value.validFrom)
        ctx.addIssue({ code: "custom", path: ["validTo"], message: "结束时间必须晚于开始时间" });
});
export const addonPublishSchema = z.object({
    addonVersionId: z.string().uuid(),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const addonVersionLifecycleSchema = z.object({
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const addonCatalogItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    kind: addonKindSchema,
    kindLabel: z.string(),
    status: z.enum(["draft", "published", "retired"]),
    versions: z.array(z.object({
        id: z.string().uuid(),
        version: z.number().int().positive(),
        status: planVersionStatusSchema,
        statusLabel: z.string(),
        changeNote: z.string(),
        billingInterval: billingIntervalSchema,
        currency: currencySchema,
        amountMinor: moneyMinorSchema,
        taxMode: taxModeSchema,
        taxRateBp: taxRateBpSchema,
        meterId: z.string().uuid().nullable().default(null),
        capabilityId: z.string().uuid().nullable().default(null),
        quantity: quantitySchema.nullable().default(null),
        targetName: z.string(),
        validFrom: at,
        validTo: atOrNull,
        publishedAt: atOrNull,
        updatedAt: at,
    })),
});
/* ------------------------------------------------------------------ *
 * 订阅
 * ------------------------------------------------------------------ */
export const subscriptionPurchaseSchema = z.object({
    tenantId: z.string().uuid(),
    planVersionId: z.string().uuid(),
    billingInterval: billingIntervalSchema,
    currency: currencySchema.default("CNY"),
    /** 加购项，可为空。 */
    addonVersionIds: z.array(z.object({ addonVersionId: z.string().uuid(), quantity: z.number().int().positive().default(1) })).max(50).default([]),
    /** seat 类加购的席位数。 */
    seats: z.number().int().positive().max(100_000).nullable().default(null),
    /** 立即生效或指定日期生效；不允许默认覆盖历史。 */
    effectiveMode: z.enum(["immediate", "scheduled"]).default("immediate"),
    effectiveAt: at.nullable().default(null),
    /** 试用天数，仅新购有效。 */
    trialDays: z.number().int().min(0).max(365).default(0),
    autoRenew: z.boolean().default(true),
    channel: purchaseChannelSchema.default("platform_sales"),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).superRefine((value, ctx) => {
    if (value.effectiveMode === "scheduled" && !value.effectiveAt) {
        ctx.addIssue({ code: "custom", path: ["effectiveAt"], message: "指定日期生效必须选择生效时间" });
    }
    if (value.effectiveMode === "immediate" && value.effectiveAt) {
        ctx.addIssue({ code: "custom", path: ["effectiveAt"], message: "立即生效不应填写生效时间" });
    }
});
export const subscriptionChangeCreateSchema = z.object({
    changeType: subscriptionChangeRequestTypeSchema,
    toPlanVersionId: z.string().uuid().nullable().default(null),
    targetChangeId: z.string().uuid().nullable().default(null),
    addonVersionIds: z.array(z.object({ addonVersionId: z.string().uuid(), quantity: z.number().int().positive().default(1) })).max(50).optional(),
    prorationMode: prorationModeSchema.default("daily"),
    effectiveMode: z.enum(["immediate", "scheduled"]).default("immediate"),
    effectiveAt: at.nullable().default(null),
    /** 手工延长天数，仅 manual_extend 使用。 */
    extendDays: z.number().int().positive().max(3_650).nullable().default(null),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).superRefine((value, ctx) => {
    if (value.changeType === "extend" && value.extendDays === null) {
        ctx.addIssue({ code: "custom", path: ["extendDays"], message: "手工延长必须填写延长天数" });
    }
    if (["upgrade", "downgrade"].includes(value.changeType) && !value.toPlanVersionId) {
        ctx.addIssue({ code: "custom", path: ["toPlanVersionId"], message: "套餐变更必须选择目标套餐版本" });
    }
    if (value.changeType === "revoke_pending" && !value.targetChangeId) {
        ctx.addIssue({ code: "custom", path: ["targetChangeId"], message: "撤销待生效变更必须选择要撤销的变更单" });
    }
    if (value.changeType !== "revoke_pending" && value.targetChangeId) {
        ctx.addIssue({ code: "custom", path: ["targetChangeId"], message: "只有撤销待生效变更可以选择目标变更单" });
    }
    if (value.effectiveMode === "scheduled" && !value.effectiveAt) {
        ctx.addIssue({ code: "custom", path: ["effectiveAt"], message: "指定日期生效必须选择生效时间" });
    }
    const addonIds = value.addonVersionIds?.map((item) => item.addonVersionId) ?? [];
    if (new Set(addonIds).size !== addonIds.length) {
        ctx.addIssue({ code: "custom", path: ["addonVersionIds"], message: "同一个加购包不能重复选择" });
    }
});
/** 变更预览：功能差异、额度差异、应付/应退差额、生效时间与受影响服务。 */
export const subscriptionChangePreviewSchema = z.object({
    capabilityDiff: z.array(z.object({
        capabilityId: z.string().uuid(),
        displayName: z.string(),
        from: capabilityInclusionSchema.nullable(),
        to: capabilityInclusionSchema.nullable(),
        effect: z.enum(["added", "removed", "downgraded", "upgraded", "unchanged"]),
    })),
    meterLimitDiff: z.array(z.object({
        meterId: z.string().uuid(),
        displayName: z.string(),
        unit: z.string(),
        fromIncluded: quantitySchema.nullable(),
        toIncluded: quantitySchema.nullable(),
        fromUnlimited: z.boolean(),
        toUnlimited: z.boolean(),
    })),
    amountDueMinor: moneyMinorSchema,
    amountRefundMinor: moneyMinorSchema,
    prorationMode: prorationModeSchema,
    prorationExplain: z.string(),
    effectiveAt: at,
    affectedServices: z.array(z.string()),
    /** 降级前检查（CP-SUB-006）。 */
    downgradeBlockers: z.array(z.object({
        code: z.string(),
        message: z.string(),
        /** 可执行的处置建议，不允许只报错。 */
        resolution: z.string(),
    })),
});
export const subscriptionChangeSubmitSchema = z.object({
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const subscriptionChangeApplySchema = z.object({
    approvalId: z.string().uuid(),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const subscriptionChangeCancelSchema = z.object({
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const subscriptionRetryProjectionSchema = z.object({
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const subscriptionLedgerQuerySchema = z.object({
    status: subscriptionStatusV2Schema.optional(),
    q: z.string().trim().max(120).optional(),
});
const subscriptionLedgerItemSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    status: subscriptionStatusV2Schema,
    statusLabel: z.string(),
    planName: z.string(),
    planVersion: z.number().int().positive(),
    billingInterval: billingIntervalSchema,
    currency: currencySchema,
    currentPeriodStart: at,
    currentPeriodEnd: at,
    autoRenew: z.boolean(),
    projectionState: z.enum(["pending", "applied", "failed"]),
    projectionError: z.string().nullable(),
    nextAction: z.string(),
    updatedAt: at,
});
export const subscriptionLedgerSchema = z.object({
    items: z.array(subscriptionLedgerItemSchema),
    queueCounts: z.record(subscriptionStatusV2Schema, z.number().int().nonnegative()),
});
export const subscriptionDetailSchema = subscriptionLedgerItemSchema.extend({
    startedAt: at,
    trialEndsAt: atOrNull,
    graceEndsAt: atOrNull,
    cancelAt: atOrNull,
    canceledAt: atOrNull,
    suspendedAt: atOrNull,
    suspendedReason: z.string().nullable(),
    endedAt: atOrNull,
    projectionAt: atOrNull,
    projectionSignature: z.string().nullable(),
    items: z.array(z.object({
        id: z.string().uuid(),
        itemType: z.enum(["plan", "addon"]),
        name: z.string(),
        quantity: z.number().int().positive(),
        unitAmountMinor: moneyMinorSchema.nullable(),
        netAmountMinor: moneyMinorSchema.nullable(),
        priceState: z.enum(["configured", "unknown"]),
        effectiveFrom: at,
        effectiveTo: atOrNull,
        snapshot: z.record(z.string(), z.unknown()),
    })),
    periods: z.array(z.object({
        id: z.string().uuid(),
        periodStart: at,
        periodEnd: at,
        status: z.enum(["upcoming", "open", "closed", "void"]),
        billingIntervalSnapshot: z.string(),
        amountMinor: moneyMinorSchema.nullable(),
        createdAt: at,
    })),
    changes: z.array(z.object({
        id: z.string().uuid(),
        changeType: subscriptionChangeTypeSchema,
        changeTypeLabel: z.string(),
        status: subscriptionChangeStatusSchema,
        statusLabel: z.string(),
        amountDueMinor: moneyMinorSchema,
        amountRefundMinor: moneyMinorSchema,
        effectiveAt: at,
        appliedAt: atOrNull,
        failureMessage: z.string().nullable(),
        reason: z.string(),
        targetChangeId: z.string().uuid().nullable(),
        createdAt: at,
    })),
});
/* ------------------------------------------------------------------ *
 * 订购单、账单与支付
 * ------------------------------------------------------------------ */
export const commercialDiscountRuleCreateSchema = z.object({
    tenantId: z.string().uuid(),
    planVersionId: z.string().uuid().nullable().default(null),
    addonVersionId: z.string().uuid().nullable().default(null),
    billingInterval: billingIntervalSchema.nullable().default(null),
    currency: currencySchema.default("CNY"),
    discountAmountMinor: moneyMinorSchema,
    startsAt: at,
    endsAt: at,
    maxRedemptions: z.number().int().positive().max(100_000).default(1),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).superRefine((value, ctx) => {
    if ((value.planVersionId === null) === (value.addonVersionId === null)) {
        ctx.addIssue({ code: "custom", path: ["planVersionId"], message: "优惠规则必须且只能选择一个套餐版本或加购版本" });
    }
    if (parseAmountMinor(value.discountAmountMinor) <= 0n) {
        ctx.addIssue({ code: "custom", path: ["discountAmountMinor"], message: "优惠金额必须大于 0" });
    }
    if (new Date(value.endsAt).getTime() <= new Date(value.startsAt).getTime()) {
        ctx.addIssue({ code: "custom", path: ["endsAt"], message: "优惠有效期结束时间必须晚于开始时间" });
    }
});
export const commercialDiscountRuleApproveSchema = z.object({
    approvalId: z.string().uuid(),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const commercialDiscountRuleViewSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    planVersionId: z.string().uuid().nullable(),
    addonVersionId: z.string().uuid().nullable(),
    targetName: z.string(),
    billingInterval: billingIntervalSchema.nullable(),
    currency: currencySchema,
    discountAmountMinor: moneyMinorSchema,
    status: z.enum(["draft", "pending_approval", "approved", "revoked"]),
    statusLabel: z.string(),
    startsAt: at,
    endsAt: at,
    maxRedemptions: z.number().int().positive(),
    redeemedCount: z.number().int().nonnegative(),
    reason: z.string(),
    approvalRequestId: z.string().uuid().nullable(),
    approvedByName: z.string().nullable(),
    approvedAt: atOrNull,
    createdAt: at,
    updatedAt: at,
});
export const purchaseOrderCreateSchema = z.object({
    tenantId: z.string().uuid(),
    subscriptionId: z.string().uuid().nullable().default(null),
    channel: purchaseChannelSchema.default("platform_sales"),
    currency: currencySchema.default("CNY"),
    billingInterval: billingIntervalSchema.default("monthly"),
    items: z.array(z.object({
        itemType: z.enum(["plan", "addon"]),
        planVersionId: z.string().uuid().nullable().default(null),
        addonVersionId: z.string().uuid().nullable().default(null),
        /** 购买份数：受控小整数，非用量金额。 */
        quantity: z.number().int().positive().max(100_000).default(1),
    })).min(1).max(100),
    /** 折扣必须有范围、有效期、审批和快照（CP-ORDER-007）。 */
    discount: z.object({
        amountMinor: moneyMinorSchema.default("0"),
        ruleId: z.string().uuid().nullable().default(null),
        approvalId: z.string().uuid().nullable().default(null),
        reason: z.string().trim().max(500).default(""),
    }).default({ amountMinor: "0", ruleId: null, approvalId: null, reason: "" }),
    taxMode: taxModeSchema.default("tax_inclusive"),
    taxRateBp: taxRateBpSchema.default(0),
    notes: z.string().trim().max(1_000).default(""),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).superRefine((value, ctx) => {
    for (const [index, item] of value.items.entries()) {
        const targets = [item.planVersionId, item.addonVersionId].filter((id) => id !== null).length;
        if (targets !== 1) {
            ctx.addIssue({ code: "custom", path: ["items", index], message: "每一项只能选择套餐版本或加购版本" });
        }
    }
    if (parseAmountMinor(value.discount.amountMinor) > 0n && !value.discount.reason) {
        ctx.addIssue({ code: "custom", path: ["discount", "reason"], message: "填写折扣必须说明原因" });
    }
    if (parseAmountMinor(value.discount.amountMinor) > 0n && (!value.discount.ruleId || !value.discount.approvalId)) {
        ctx.addIssue({ code: "custom", path: ["discount", "ruleId"], message: "使用合同优惠必须选择已批准的优惠规则" });
    }
});
/**
 * 线下确认收款（CP-ORDER-005）。
 * 第一阶段没有在线支付，此接口必须留下凭证、操作者和确认时间，
 * 页面不得把它渲染成「在线支付成功」。
 */
export const offlinePaymentConfirmSchema = z.object({
    /** 收款凭证号或流水号。 */
    confirmationReference: z.string().trim().min(4).max(200),
    /** 收款凭证描述，进入审计，不展示给无关角色。 */
    evidence: z.string().trim().min(8).max(1_000),
    paidAt: at,
    approvalId: z.string().uuid(),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const refundRequestSchema = z.object({
    paymentTransactionId: z.string().uuid(),
    amountMinor: moneyMinorSchema,
    reason: reasonSchema,
    /** 是否同步处理订阅影响（暂停、降级或取消）。 */
    subscriptionHandling: refundSubscriptionHandlingSchema.default("none"),
    idempotencyKey: idempotencyKeySchema,
});
export const refundDecisionSchema = z.object({
    decision: z.enum(["approved", "rejected"]),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const refundExecuteSchema = z.object({
    result: z.enum(["succeeded", "failed"]),
    /** 线下退款凭证号或回单号。成功时必填。 */
    executionReference: z.string().trim().min(4).max(200).nullable().default(null),
    /** 线下退款证据说明。成功时必填。 */
    executionEvidence: z.string().trim().min(8).max(1_000).nullable().default(null),
    /** 失败原因。失败时必填。 */
    failureMessage: z.string().trim().min(8).max(1_000).nullable().default(null),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).superRefine((value, ctx) => {
    if (value.result === "succeeded") {
        if (!value.executionReference)
            ctx.addIssue({ code: "custom", path: ["executionReference"], message: "退款成功必须填写退款凭证号" });
        if (!value.executionEvidence)
            ctx.addIssue({ code: "custom", path: ["executionEvidence"], message: "退款成功必须填写退款证据" });
    }
    if (value.result === "failed" && !value.failureMessage) {
        ctx.addIssue({ code: "custom", path: ["failureMessage"], message: "退款失败必须填写失败原因" });
    }
});
export const billingProfileUpsertSchema = z.object({
    tenantId: z.string().uuid(),
    companyLegalName: z.string().trim().max(200).optional(),
    taxNumber: z.string().trim().max(60).optional(),
    billingContactName: z.string().trim().max(80).optional(),
    billingContactEmail: z.string().trim().email().max(254).or(z.literal("")).optional(),
    billingContactPhone: z.string().trim().max(32).optional(),
    billingAddress: z.string().trim().max(500).optional(),
    invoicePreference: z.enum(["none", "vat_general", "vat_special"]).optional(),
    bankName: z.string().trim().max(120).optional(),
    /** 银行账号只接受掩码，服务端拒绝完整账号。 */
    bankAccountMask: z.string().trim().max(40).optional(),
    taxMode: taxModeSchema.optional(),
    taxRateBp: taxRateBpSchema.optional(),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const invoicePreferenceLabels = {
    none: "暂不开票",
    vat_general: "增值税普通发票",
    vat_special: "增值税专用发票",
};
export const taxModeLabels = {
    tax_inclusive: "含税",
    tax_exclusive: "未税",
};
export const billingProfileSnapshotSchema = z.object({
    configured: z.boolean(),
    companyLegalName: z.string(),
    invoicePreference: z.enum(["none", "vat_general", "vat_special"]),
    invoicePreferenceLabel: z.string(),
    taxMode: taxModeSchema,
    taxModeLabel: z.string(),
    taxRateBp: taxRateBpSchema,
    taxNumberMasked: z.string(),
    billingContactName: z.string(),
    billingContactEmailMasked: z.string(),
    billingContactPhoneMasked: z.string(),
    billingAddressSummary: z.string(),
    bankName: z.string(),
    bankAccountMask: z.string(),
    updatedAt: atOrNull,
});
export const billingProfileViewSchema = billingProfileSnapshotSchema.extend({
    tenantName: z.string(),
    editable: z.object({
        companyLegalName: z.string(),
        billingContactName: z.string(),
        billingAddress: z.string(),
        invoicePreference: z.enum(["none", "vat_general", "vat_special"]),
        bankName: z.string(),
        bankAccountMask: z.string(),
        taxMode: taxModeSchema,
        taxRateBp: taxRateBpSchema,
    }),
});
/** 账单调整：已出账内容不可直接覆盖，只能追加调整行。 */
export const invoiceAdjustSchema = z.object({
    lineType: z.enum(["adjustment", "discount"]),
    description: z.string().trim().min(2).max(500),
    amountMinor: signedMoneyMinorSchema,
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const invoiceIssueSchema = z.object({
    periodStart: at,
    periodEnd: at,
    dueAt: at,
    idempotencyKey: idempotencyKeySchema,
}).refine((value) => value.periodEnd > value.periodStart, {
    message: "账期结束时间必须晚于开始时间",
    path: ["periodEnd"],
});
/* ------------------------------------------------------------------ *
 * 用量与额度
 * ------------------------------------------------------------------ */
export const usageSummaryQuerySchema = z.object({
    tenantId: z.string().uuid(),
    periodStart: at,
    periodEnd: at,
}).refine((value) => value.periodEnd > value.periodStart, {
    message: "账期结束时间必须晚于开始时间",
    path: ["periodEnd"],
});
export const usageSummaryRecomputeSchema = z.object({
    tenantId: z.string().uuid(),
    meterId: z.string().uuid().nullable().default(null),
    periodStart: at,
    periodEnd: at,
    reason: reasonSchema,
    approvalId: z.string().uuid(),
    idempotencyKey: idempotencyKeySchema,
}).refine((value) => value.periodEnd > value.periodStart, {
    message: "账期结束时间必须晚于开始时间",
    path: ["periodEnd"],
});
/** 账期用量视图。没有数据时必须显式说明「尚未接入」，不得用 0 冒充。 */
export const usageSummaryItemSchema = z.object({
    meterId: z.string().uuid(),
    meterKey: z.string(),
    displayName: z.string(),
    unit: z.string(),
    aggregation: meterAggregationSchema,
    source: meterSourceSchema,
    decimalScale: quantityDecimalScaleSchema,
    billable: z.boolean(),
    dataAvailable: z.boolean(),
    includedQuantity: quantitySchema.nullable(),
    unlimited: z.boolean(),
    hardLimitQuantity: quantitySchema.nullable(),
    usedQuantity: quantitySchema,
    todayQuantity: quantitySchema,
    monthQuantity: quantitySchema,
    remainingQuantity: quantitySchema.nullable(),
    softLimitPercent: z.number().int().nullable(),
    overagePolicy: overagePolicySchema,
    overageUnitPriceMinor: moneyMinorSchema.nullable(),
    /** 按当前消耗速度预测的耗尽日；数据不足时为 null，页面不得编造。 */
    forecastExhaustedAt: atOrNull,
    dataLatencyMinutes: z.number().int(),
    periodStart: at,
    periodEnd: at,
    updatedAt: at,
});
export const usageErpReadbackItemSchema = z.object({
    meterKey: z.string().regex(/^[a-z0-9][a-z0-9_.:-]{1,119}$/),
    unit: z.string().trim().min(1).max(40),
    decimalScale: quantityDecimalScaleSchema,
    quantity: quantitySchema,
});
export const usageErpReadbackCompareSchema = z.object({
    tenantId: z.string().uuid(),
    periodStart: at,
    periodEnd: at,
    items: z.array(usageErpReadbackItemSchema).min(1).max(500),
    idempotencyKey: idempotencyKeySchema,
}).refine((value) => value.periodEnd > value.periodStart, {
    message: "账期结束时间必须晚于开始时间",
    path: ["periodEnd"],
});
export const usageErpReadbackComparisonSchema = z.object({
    tenantId: z.string().uuid(),
    periodStart: at,
    periodEnd: at,
    consistent: z.boolean(),
    checkedAt: at,
    items: z.array(z.object({
        meterKey: z.string(),
        unit: z.string(),
        decimalScale: quantityDecimalScaleSchema,
        controlPlaneQuantity: quantitySchema,
        erpQuantity: quantitySchema,
        deltaQuantity: signedMoneyMinorSchema,
        status: z.enum(["matched", "unit_mismatch", "scale_mismatch", "quantity_mismatch", "missing_in_control_plane", "missing_in_erp"]),
    })),
});
export const usageAlertPolicyUpsertSchema = z.object({
    tenantId: z.string().uuid(),
    meterId: z.string().uuid(),
    thresholdPercent: z.number().int().min(1).max(100),
    channel: z.enum(["email", "sms", "webhook", "internal"]),
    recipientKind: z.enum(["billing_profile", "admin", "tenant_contact"]),
    recipientRef: z.string().trim().min(1).max(120),
    suppressionMinutes: z.number().int().min(0).max(43_200).default(1440),
    status: z.enum(["active", "paused"]).default("active"),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
});
export const usageAlertPolicyViewSchema = usageAlertPolicyUpsertSchema.omit({ reason: true, idempotencyKey: true }).extend({
    id: z.string().uuid(),
    meterName: z.string(),
    recipientName: z.string(),
    lastTriggeredAt: atOrNull,
    updatedAt: at,
});
export const usageAdjustmentCreateSchema = z.object({
    tenantId: z.string().uuid(),
    meterId: z.string().uuid(),
    periodStart: at,
    periodEnd: at,
    /** 调整量：可正可负的整数字符串。 */
    quantityDelta: signedMoneyMinorSchema,
    sourceIdempotencyKey: z.string().trim().min(8).max(160).nullable().default(null),
    approvalId: z.string().uuid(),
    reason: reasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).refine((value) => value.periodEnd > value.periodStart, {
    message: "账期结束时间必须晚于开始时间",
    path: ["periodEnd"],
}).refine((value) => parseAmountMinor(value.quantityDelta) !== 0n, {
    message: "调整量不能为 0",
    path: ["quantityDelta"],
});
export const quotaOverrideCreateSchema = z.object({
    tenantId: z.string().uuid(),
    meterId: z.string().uuid(),
    unlimited: z.boolean().default(false),
    limitQuantity: quantitySchema.nullable().default(null),
    softLimitPercent: z.number().int().min(1).max(100).nullable().default(null),
    startsAt: at,
    reason: reasonSchema,
    /** 强制设置失效时间，不允许长期例外（CP-USAGE-006）。 */
    endsAt: at,
    approvalId: z.string().uuid(),
    idempotencyKey: idempotencyKeySchema,
}).refine((value) => value.unlimited || value.limitQuantity !== null, {
    message: "必须填写额度数量或选择不限量",
    path: ["limitQuantity"],
}).refine((value) => value.endsAt > value.startsAt, {
    message: "失效时间必须晚于开始时间",
    path: ["endsAt"],
});
export const usageAdjustmentViewSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    meterId: z.string().uuid(),
    meterName: z.string(),
    unit: z.string(),
    decimalScale: quantityDecimalScaleSchema,
    periodStart: at,
    periodEnd: atOrNull,
    quantityDelta: signedMoneyMinorSchema,
    reason: z.string(),
    sourceIdempotencyKey: z.string().nullable(),
    approvalId: z.string().uuid().nullable(),
    createdAt: at,
});
export const quotaOverrideViewSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    meterId: z.string().uuid(),
    meterName: z.string(),
    unit: z.string(),
    decimalScale: quantityDecimalScaleSchema,
    unlimited: z.boolean(),
    limitQuantity: quantitySchema.nullable(),
    softLimitPercent: z.number().int().nullable(),
    overagePolicy: overagePolicySchema.nullable(),
    startsAt: at,
    endsAt: at,
    releasedAt: atOrNull,
    reason: z.string(),
    approvalId: z.string().uuid().nullable(),
});
export const usageCenterQuerySchema = z.object({
    tenantId: z.string().uuid().optional(),
});
export const usageCenterViewSchema = z.object({
    tenantId: z.string().uuid().nullable(),
    tenantName: z.string(),
    periodStart: at,
    periodEnd: at,
    items: z.array(usageSummaryItemSchema),
    alertPolicies: z.array(usageAlertPolicyViewSchema),
    quotaOverrides: z.array(quotaOverrideViewSchema),
    adjustments: z.array(usageAdjustmentViewSchema),
    erpReadback: usageErpReadbackComparisonSchema.nullable(),
    updatedAt: at,
});
export const entitlementOverrideCreateSchema = z.object({
    tenantId: z.string().uuid(),
    capabilityId: z.string().uuid(),
    effect: overrideEffectSchema,
    reason: reasonSchema,
    endsAt: at,
    idempotencyKey: idempotencyKeySchema,
});
/* ------------------------------------------------------------------ *
 * 对账
 * ------------------------------------------------------------------ */
export const reconciliationRunCreateSchema = z.object({
    runDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    scope: z.enum(["daily", "manual"]).default("manual"),
    idempotencyKey: idempotencyKeySchema,
});
export const reconciliationDiffResolveSchema = z.object({
    status: z.enum(["resolved", "ignored"]),
    resolutionNote: z.string().trim().min(8).max(1_000),
    idempotencyKey: idempotencyKeySchema,
});
/** 对账差异（异常队列条目）。 */
export const reconciliationDiffSchema = z.object({
    id: z.string().uuid(),
    objectType: z.enum(["order", "payment", "invoice", "subscription", "usage"]),
    objectId: z.string(),
    diffType: z.string(),
    expected: z.string(),
    actual: z.string(),
    severity: z.enum(["low", "medium", "high"]),
    status: reconciliationDiffStatusSchema,
    createdAt: at,
});
export const purchaseOrderLedgerQuerySchema = z.object({
    status: purchaseOrderStatusSchema.optional(),
    q: z.string().trim().max(120).optional(),
});
const billingMoneyFields = {
    currency: currencySchema,
    subtotalMinor: moneyMinorSchema,
    discountMinor: moneyMinorSchema,
    taxMinor: moneyMinorSchema,
    totalMinor: moneyMinorSchema,
};
export const purchaseOrderLedgerItemSchema = z.object({
    id: z.string().uuid(),
    orderNo: z.string(),
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    status: purchaseOrderStatusSchema,
    statusLabel: z.string(),
    channel: purchaseChannelSchema,
    channelLabel: z.string(),
    ...billingMoneyFields,
    confirmationReference: z.string().nullable(),
    confirmedAt: atOrNull,
    nextAction: z.string(),
    createdAt: at,
    updatedAt: at,
});
export const invoiceViewSchema = z.object({
    id: z.string().uuid(),
    invoiceNo: z.string(),
    tenantName: z.string(),
    status: invoiceStatusSchema,
    statusLabel: z.string(),
    ...billingMoneyFields,
    amountPaidMinor: moneyMinorSchema,
    amountRefundedMinor: moneyMinorSchema,
    periodStart: at,
    periodEnd: at,
    issuedAt: atOrNull,
    dueAt: atOrNull,
    paidAt: atOrNull,
    billingProfileSnapshot: billingProfileSnapshotSchema.nullable().default(null),
});
export const paymentTransactionViewSchema = z.object({
    id: z.string().uuid(),
    tenantName: z.string(),
    orderNo: z.string().nullable(),
    invoiceNo: z.string().nullable(),
    method: paymentMethodSchema,
    methodLabel: z.string(),
    status: paymentStatusSchema,
    statusLabel: z.string(),
    amountMinor: moneyMinorSchema,
    currency: currencySchema,
    confirmationReference: z.string().nullable(),
    occurredAt: at,
});
export const refundTransactionViewSchema = z.object({
    id: z.string().uuid(),
    tenantName: z.string(),
    invoiceNo: z.string().nullable(),
    status: refundStatusSchema,
    statusLabel: z.string(),
    subscriptionHandling: refundSubscriptionHandlingSchema,
    amountMinor: moneyMinorSchema,
    currency: currencySchema,
    reason: z.string(),
    executionReference: z.string().nullable(),
    executionEvidence: z.string().nullable(),
    failureMessage: z.string().nullable(),
    requestedAt: at,
    approvedAt: atOrNull,
    executedAt: atOrNull,
});
export const billingNotificationViewSchema = z.object({
    id: z.string().uuid(),
    tenantName: z.string().nullable(),
    category: z.string(),
    channel: z.string(),
    recipientMasked: z.string(),
    subject: z.string(),
    status: z.string(),
    attempts: z.number().int().nonnegative(),
    sentAt: atOrNull,
    lastError: z.string().nullable(),
    createdAt: at,
});
export const reconciliationRunViewSchema = z.object({
    id: z.string().uuid(),
    runDate: z.string(),
    scope: z.enum(["daily", "manual"]),
    status: z.string(),
    checkedOrders: z.number().int().nonnegative(),
    checkedPayments: z.number().int().nonnegative(),
    checkedInvoices: z.number().int().nonnegative(),
    checkedSubscriptions: z.number().int().nonnegative(),
    diffCount: z.number().int().nonnegative(),
    finishedAt: atOrNull,
});
export const purchaseOrderDetailSchema = purchaseOrderLedgerItemSchema.extend({
    notes: z.string().nullable(),
    billingProfileSnapshot: billingProfileSnapshotSchema.nullable().default(null),
    items: z.array(z.object({
        id: z.string().uuid(),
        itemType: z.enum(["plan", "addon"]),
        displayName: z.string(),
        quantity: z.number().int().positive(),
        unitAmountMinor: moneyMinorSchema,
        discountMinor: moneyMinorSchema,
        taxMinor: moneyMinorSchema,
        netMinor: moneyMinorSchema,
    })),
    invoices: z.array(invoiceViewSchema),
    payments: z.array(paymentTransactionViewSchema),
    refunds: z.array(refundTransactionViewSchema),
});
export const ordersBillingConsoleSchema = z.object({
    paymentMode: z.literal("offline_confirm"),
    items: z.array(purchaseOrderLedgerItemSchema),
    queueCounts: z.record(purchaseOrderStatusSchema, z.number().int().nonnegative()),
    invoices: z.array(invoiceViewSchema),
    payments: z.array(paymentTransactionViewSchema),
    refunds: z.array(refundTransactionViewSchema),
    notifications: z.array(billingNotificationViewSchema),
    reconciliation: z.object({
        latestRun: reconciliationRunViewSchema.nullable(),
        diffs: z.array(reconciliationDiffSchema),
    }),
});
